/**
 * DEFEAT SCENE - Pantalla de Derrota
 * Mostrada cuando el jugador pierde (integridad < 70% o tiempo/votos agotados)
 */

import Phaser from "phaser";
import { SCENES, TEXTS, COLORS, MESSAGES } from "../utils/constants.js";
import { fillTemplate } from "../utils/helpers.js";
import { getHouseAssets } from "../utils/constants.js";

export default class DefeatScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENES.DEFEAT });
  }

  init(data) {
    // Recibir datos de la derrota
    this.defeatData = {
      integrity: data.integrity || 0,
      votesUsed: data.votesUsed || 0,
      totalVotes: data.totalVotes || 20,
      votesLeft: data.votesLeft || 0,
      cracksLeft: data.cracksLeft || 0,
      round: data.round || 1,
      reason: data.reason || "low_integrity", // 'low_integrity', 'time_out', 'votes_left', 'critical_failed'
    };

    // Guardar ronda actual para usar casa correcta
    this.currentRound = data.round || 1;
  }

  create() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // ========================================
    // BACKGROUND DE DERROTA
    // ========================================
    const background = this.add.image(width / 2, height / 2, "bg_lose");

    // Escalar para cubrir toda la pantalla
    const scaleX = width / background.width;
    const scaleY = height / background.height;
    const scale = Math.max(scaleX, scaleY);
    background.setScale(scale);
    background.setDepth(-2); // Más atrás que la casa

    console.log("🎨 Background de derrota cargado");

    // Casa deteriorada de la ronda actual (encima del bg_lose)
    const houseAssets = getHouseAssets(this.currentRound);
    const bgHouse = this.add.image(width / 2, height / 2, houseAssets.bad);
    bgHouse.setDisplaySize(width * 0.7, height * 0.7);
    bgHouse.setAlpha(0.9);
    bgHouse.setDepth(-1); // Entre el background y el overlay

    console.log(
      `💀 DefeatScene con casa de Ronda ${this.currentRound}: ${houseAssets.era}`
    );

    // 🔥 Overlay con figuras (PNG sin fondo) ENCIMA de la casa
    const defeatOverlay = this.add.image(
      width / 2,
      height / 2,
      "defeat_overlay"
    );

    // Escalar igual que el background para que coincidan
    const overlayScaleX = width / defeatOverlay.width;
    const overlayScaleY = height / defeatOverlay.height;
    const overlayScale = Math.max(overlayScaleX, overlayScaleY);
    defeatOverlay.setScale(overlayScale);

    defeatOverlay.setDepth(0); // Encima de la casa (-1) pero debajo del overlay oscuro

    console.log("🔥 Overlay de derrota renderizado encima de la casa");

    // Overlay oscuro para legibilidad del texto
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.2);
    overlay.setOrigin(0);
    overlay.setDepth(1);

    // Efecto de pantalla rota
    //this.createBrokenEffect();

    // Rótulo "CLAUSURADO"
    this.createClausuradoSign(width / 2, height * 0.25);

    // Mensaje personalizado según razón de derrota
    this.showDefeatMessage(width / 2, height * 0.45);

    // Estadísticas finales
    this.showStats(width / 2, height * 0.55);

    // Botón REINTENTAR
    this.createRetryButton(width / 2, height * 0.8);

    // Botón COMPARTIR
    this.createShareButton(width / 2, height * 0.9);

    console.log("💀 Derrota -", this.defeatData.reason);
  }

  /**
   * Crear efecto de pantalla rota
   */
  createBrokenEffect() {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Grietas visuales
    const graphics = this.add.graphics();
    graphics.lineStyle(3, 0xff0000, 0.5);

    for (let i = 0; i < 5; i++) {
      graphics.beginPath();
      graphics.moveTo(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height)
      );
      graphics.lineTo(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height)
      );
      graphics.strokePath();
    }
  }

  /**
   * Crear rótulo "CLAUSURADO"
   */
  createClausuradoSign(x, y) {
    // Fondo del rótulo (estilo municipalidad)
    const signBg = this.add.rectangle(x, y, 400, 100, 0xdc2626);
    signBg.setStrokeStyle(8, 0xffffff);

    // Texto CLAUSURADO
    const signText = this.add.text(x, y, TEXTS.CLAUSURADO, {
      fontSize: "48px",
      fontWeight: "bold",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    signText.setOrigin(0.5);

    // Rotación ligera para efecto de rótulo
    signBg.setRotation(-0.05);
    signText.setRotation(-0.05);

    // Animación de temblor
    this.tweens.add({
      targets: [signBg, signText],
      rotation: -0.08,
      duration: 200,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Mostrar mensaje personalizado según razón de derrota
   */
  showDefeatMessage(x, y) {
    let message = "";

    switch (this.defeatData.reason) {
      case "votes_left":
        message = fillTemplate(MESSAGES.DEFEAT.VOTES_LEFT, {
          votes: this.defeatData.votesLeft,
        });
        break;

      case "time_out":
        message = MESSAGES.DEFEAT.TIME_OUT;
        break;

      case "critical_failed":
        message = fillTemplate(MESSAGES.DEFEAT.CRITICAL_FAILED, {
          component: this.defeatData.component || "Constitución",
        });
        break;

      case "low_integrity":
      default:
        message = fillTemplate(MESSAGES.DEFEAT.LOW_INTEGRITY, {
          integrity: Math.round(this.defeatData.integrity),
        });
        break;
    }

    const messageText = this.add.text(x, y, message, {
      fontSize: "26px",
      fontWeight: "bold",
      color: "#dc2626",
      fontFamily: "Arial",
      align: "center",
      wordWrap: { width: 600 },
      stroke: "#000000",
      strokeThickness: 3,
    });
    messageText.setOrigin(0.5);
  }

  /**
   * Mostrar estadísticas finales
   */
  showStats(x, y) {
    const stats = [
      `Integridad final: ${Math.round(this.defeatData.integrity)}%`,
      `Votos usados: ${this.defeatData.votesUsed}/${this.defeatData.totalVotes}`,
      `Grietas sin reparar: ${this.defeatData.cracksLeft}`,
      `Ciclos completados: ${this.defeatData.round - 1}`,
    ];

    stats.forEach((stat, index) => {
      const statText = this.add.text(x, y + index * 30, stat, {
        fontSize: "18px",
        color: "#ffffff",
        fontFamily: "Arial",
        backgroundColor: "#00000066",
        padding: { x: 15, y: 5 },
      });
      statText.setOrigin(0.5);
    });
  }

  /**
   * Crear botón REINTENTAR
   */
  createRetryButton(x, y) {
    const button = this.add.rectangle(x, y, 200, 45, 0xdc2626);
    button.setStrokeStyle(4, 0xffffff);
    button.setInteractive({ useHandCursor: true });

    const buttonText = this.add.text(x, y, TEXTS.RETRY, {
      fontSize: "26px",
      fontWeight: "bold",
      color: "#ffffff",
      fontFamily: "Arial",
    });
    buttonText.setOrigin(0.5);

    // Efectos hover
    button.on("pointerover", () => {
      button.setFillStyle(0xff4444);
      this.tweens.add({
        targets: [button, buttonText],
        scale: 1.1,
        duration: 100,
      });
    });

    button.on("pointerout", () => {
      button.setFillStyle(0xdc2626);
      this.tweens.add({
        targets: [button, buttonText],
        scale: 1.0,
        duration: 100,
      });
    });

    // Click - Reiniciar juego
    button.on("pointerdown", () => {
      this.retry();
    });
  }

  /**
   * Crear botón COMPARTIR
   */
  createShareButton(x, y) {
    const buttonText = this.add.text(x, y, TEXTS.SHARE, {
      fontSize: "20px", // ← Aumentado de 18 a 20
      color: "#ffffff", // ← Cambiado a blanco
      fontFamily: "Arial",
      stroke: "#000000", // ← Borde negro
      strokeThickness: 3,
    });
    buttonText.setOrigin(0.5);
    buttonText.setInteractive({ useHandCursor: true });

    buttonText.on("pointerover", () => {
      buttonText.setColor("#48bb78"); // ← Verde en hover
      buttonText.setScale(1.05); // ← Efecto de agrandar
    });

    buttonText.on("pointerout", () => {
      buttonText.setColor("#ffffff"); // ← Volver a blanco
      buttonText.setScale(1.0); // ← Volver a tamaño normal
    });

    buttonText.on("pointerdown", () => {
      this.shareResults();
    });
  }

  /**
   * Reintentar juego
   */
  retry() {
    console.log("🔄 Reintentando...");

    this.cameras.main.fade(300, 0, 0, 0);

    this.time.delayedCall(300, () => {
      this.scene.start(SCENES.GAME);
    });
  }

  /**
   * Compartir resultados
   */
  shareResults() {
    const cyclesCompleted = Math.max(0, this.defeatData.round - 1);
    const shareText =
      cyclesCompleted > 0
        ? `Mantuve la democracia viva por ${cyclesCompleted} ciclos en Mala Hierba 🏛️\n¿Puedes hacerlo mejor?\n\n[URL del juego]`
        : `Intenté mantener la democracia en Mala Hierba 🏛️\n¡Es más difícil de lo que parece!\n\n[URL del juego]`;

    console.log("📤 Compartir:", shareText);

    // Por ahora, copiar al portapapeles
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareText);
      alert("¡Texto copiado al portapapeles!");
    } else {
      alert(shareText);
    }
  }
}
